<template>
  <div>
    <h4>Quản lý tài liệu</h4>
    <VueGoodTable
      mode="remote"
      @on-page-change="onPageChange"
      @on-sort-change="onSortChange"
      @on-per-page-change="onPerPageChange"
      @on-search="onSearch"
      :isLoading.sync="isLoading"
      :columns="columns"
      :rows="documents"
      :totalRows="totalRecords"
      :line-numbers="true"
      :search-options="{
        enabled: true,
        placeholder: 'Tìm kiếm',
      }"
      :pagination-options="{
        enabled: true,
        mode: 'records',
        rowsPerPageLabel: 'Số hàng mỗi trang',
        nextLabel: 'Trang sau',
        prevLabel: 'Trang trước',
        ofLabel: 'trong',
      }"
    >
      <div slot="emptystate">Danh sách tài liệu trống</div>

      <template slot="table-row" slot-scope="props">
        <div v-if="props.column.field === 'action'" class="d-flex flex-row justify-content-between">
          <FAIcon
            icon="fa fa-info-circle"
            title="Xem thông tin tài liệu"
            :color="Colors.INFO"
            @click="$refs[refs.DOCUMENT_INFO_MODAL].showModal(props.row)"
            :disabled="props.row.status !== DocumentState.COMPLETE_PROCESS"
          />
          <FAIcon
            icon="fas fa-file-download"
            title="Tải tài liệu gốc"
            :color="Colors.RAW"
            @click="handleDownloadDocument(props.row.slug, props.row.name, DownloadType.RAW)"
            :disabled="props.row.status === DocumentState.DELETED"
          />

          <FAIcon
            icon="fas fa-chart-pie"
            title="Tải báo cáo"
            :color="Colors.REPORT"
            @click="$refs[refs.DOCUMENT_REPORT_MODAL].showModal(props.row)"
            :disabled="props.row.status !== DocumentState.COMPLETE_PROCESS"
          />
        </div>

        <span v-else-if="props.column.field === 'title'">
          <CommonLink
            :to="Paths.DOCUMENT_RESULT_VIEW(DocumentTypes.DOCUMENT, props.row.slug)"
            :disable="props.row.status !== DocumentState.COMPLETE_PROCESS"
          >
            {{ props.row.title }}
          </CommonLink>
          <br />
          <span>{{ props.row.author }}</span>
          <span> - </span>
          <span>{{ props.row.userEmail }}</span>
        </span>
        <span v-else-if="props.column.field === 'score'">
          <span v-if="props.row.score !== null"> {{ props.row.score }} % </span>
          <span v-if="props.row.abnormalPercent >= 10">
            <el-tooltip
              class="item"
              effect="dark"
              :content="`Phát hiện ${props.row['abnormalPercent']} % bất thường trong tài liệu. Chọn để tải báo cáo`"
              placement="top-start"
            >
              <FAIcon icon="fas fa-exclamation-triangle" @click="handleDownloadAbnormalReport(props.row.slug)" />
            </el-tooltip>
          </span>
        </span>
        <span v-else-if="props.column.field === 'status'">
          <DocumentStatus :status="props.row.status" />
          <FAIcon
            v-if="props.row.status === DocumentState.ERROR"
            icon="fa fa-redo"
            title="Kiểm tra lại"
            :color="Colors.RETRY"
            @click="$refs[refs.DOCUMENT_CHECK_MODAL].openModal(props.row)"
          />
        </span>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>
      <template slot="loadingContent">
        <span class="vgt-loading__content"> Đang tải... </span>
      </template>
    </VueGoodTable>
    <DocumentInfoModal :ref="refs.DOCUMENT_INFO_MODAL" />
    <DocumentDeleteModal :ref="refs.DOCUMENT_DELETE_MODAL" @success="loadItems" />
    <DocumentSendModal :ref="refs.DOCUMENT_SEND_MODAL" @success="loadItems" />
    <DocumentCheckModal :ref="refs.DOCUMENT_CHECK_MODAL" @success="loadItems" />
    <DocumentReportModal :ref="refs.DOCUMENT_REPORT_MODAL" @success="loadItems" />
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import '@/assets/css/vue-good-table.css'
import 'moment/locale/vi'
import { DocumentState, DocumentTypes, DownloadType, Table } from '@/constants/DoITConstant'
import { apiDownloadDocumentAsArrayBuffer, apiGetDocumentStateByStateId } from '@/services/document_management_service'
import { pushArraybuffer2Client } from '@/helpers/DownloadHelper'
import { apiGetAllDocumentAdmin } from '@/services/admin_dashboard_service'
import truncate from 'vue-truncate-collapsed'
import DocumentReportModal from '@/components/DocumentManagement/action-modals/DocumentReportModal.vue'
import DocumentCheckModal from '@/components/DocumentManagement/action-modals/DocumentCheckModal.vue'
import DocumentInfoModal from '@/components/DocumentManagement/action-modals/DocumentInfoModal.vue'
import DocumentSendModal from '@/components/DocumentManagement/action-modals/DocumentSendModal.vue'
import DocumentDeleteModal from '@/components/DocumentManagement/action-modals/DocumentDeleteModal.vue'
import DocumentEditModal from '@/components/DocumentManagement/action-modals/DocumentEditModal.vue'
import FAIcon from '@/components/common/FAIcon.vue'
import { formatDateTime } from '@/helpers/timeHelper.js'
import { Paths } from '@/routes/paths.js'
import { Colors } from '@/constants/DoITConstant.js'
import DocumentStatus from '@/components/common/document/DocumentStatus.vue'
import CommonLink from '@/components/common/CommonLink.vue'

export default {
  name: 'DocumentsList',
  components: {
    DocumentStatus,
    DocumentReportModal,
    DocumentCheckModal,
    DocumentInfoModal,
    DocumentSendModal,
    DocumentDeleteModal,
    DocumentEditModal,
    VueGoodTable,
    truncate,
    FAIcon,
    CommonLink,
  },
  props: {},
  data: function () {
    return {
      columns: [],
      DownloadType: DownloadType,
      totalRecords: 0,
      DocumentState,
      serverParams: {
        sortAscending: false,
        sortField: 'uploadTime',
        page: 0,
        size: 10,
      },
      documents: [],
      interval: null,
      isLoading: true,
      refs: {
        DOCUMENT_INFO_MODAL: 'documentInfoModal',
        DOCUMENT_EDIT_MODAL: 'documentEditModal',
        DOCUMENT_DELETE_MODAL: 'documentDeleteModal',
        DOCUMENT_CHECK_MODAL: 'documentCheckModal',
        DOCUMENT_REPORT_MODAL: 'documentReportModal',
      },
      Paths,
      Colors,
      DocumentTypes,
    }
  },
  mounted() {
    this.columns = [
      {
        label: 'Tiêu đề',
        field: 'title',
        filterable: true,
      },
      {
        label: 'Độ trùng lặp',
        field: 'score',
        filterable: false,
      },
      {
        label: 'Lỗi chính tả',
        field: 'spellError',
        filterable: false,
      },
      {
        label: 'Số trang',
        field: 'page',
        filterable: false,
      },
      {
        label: 'TG tải lên',
        field: 'uploadTime',
        formatFn: formatDateTime,
      },
      {
        label: 'TG kết thúc',
        field: 'completeTime',
        formatFn: formatDateTime,
      },
      {
        label: 'Trạng thái',
        field: 'status',
      },
      {
        label: 'Hành động',
        field: 'action',
      },
    ]

    this.loadItems()
    this.interval = setInterval(() => {
      if (this.selectedRows.length === 0) {
        this.loadItems()
      }
    }, Table.RELOAD_INTERVAL)
  },
  destroyed() {
    clearInterval(this.interval)
  },
  methods: {
    handleDownloadAbnormalReport(slug) {
      this.handleDownloadDocument(slug, 'abnormal_' + slug + '.pdf', DownloadType.ABNORMAL_REPORT)
    },
    async handleDownloadDocument(slug, name, type) {
      const loading = this.$loading({ text: 'Đang tải tài liệu' })
      let res = await apiDownloadDocumentAsArrayBuffer(slug, type)
      pushArraybuffer2Client(res, name)
      loading.close()
    },

    onSearch(params) {
      this.serverParams.searchKey = params.searchTerm
      this.loadItems()
    },

    onPageChange(params) {
      this.serverParams.page = params.currentPage - 1
      this.loadItems()
    },

    onPerPageChange(params) {
      this.serverParams.size = params.currentPerPage
      this.loadItems()
    },

    onSortChange(params) {
      this.serverParams.sortAscending = params[0].type === 'asc'
      this.serverParams.sortField = params[0].field
      this.serverParams.page = 0
      this.loadItems()
    },
    async loadItems() {
      this.isLoading = true
      let responseAllDocument = await apiGetAllDocumentAdmin(this.serverParams)
      this.documents = responseAllDocument.data
      this.totalRecords = responseAllDocument.meta.total
      this.isLoading = false
    },
  },
}
</script>
